import { useCallback, useEffect, useState } from 'react';

import { useToggle } from 'react-use';

import { showNotificationAsync } from '../../../data-layer/notifications/actions';
import { useDispatch } from '../../../data-layer/useDispatch';
import { CollectionPersistenceUI } from '../../../ui/users/types';
import { EventSource } from '../../../utils/analyticsService';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTrackEvents } from './useTrackEvents';

export interface UseCollectionInfoProps {
    initialValues?: CollectionPersistenceUI;
    getSuggestMethod?: () => Promise<string[]>;
    needLoadSuggests?: boolean;
    screen: EventSource;
    mode: 'edit' | 'create';
    id?: string;
}

export const useCollectionInfo = ({
    initialValues,
    getSuggestMethod,
    needLoadSuggests,
    screen,
    mode,
    id,
}: UseCollectionInfoProps) => {
    const dispatch = useDispatch();
    const showInFeedEnabled = String(useExperimentFlag('show_in_feed_enabled')) === '1';

    const { trackDisplayNameEdit, trackIsPublicEdit, trackShowInFeedEdit } = useTrackEvents({
        id,
        screen,
        mode,
    });

    const [isPublic, toggleIsPublic] = useToggle(initialValues?.isPublic ?? true);
    const canShowInFeed = Boolean(
        showInFeedEnabled &&
            isPublic &&
            initialValues?.numberOfPosts &&
            initialValues.numberOfPosts > 0,
    );

    const [showInFeed, setShowInFeed] = useState(
        (canShowInFeed && initialValues?.feedStatus?.showInFeed) ?? true,
    );
    const [collectionName, setCollectionName] = useState(initialValues?.displayName || '');
    const [suggests, setSuggests] = useState<string[]>([]);
    const [suggestsLoading, setSuggestsLoading] = useState(false);
    const [currentSuggest, setCurrentSuggest] = useState('');

    const values = {
        collectionName,
        showInFeed,
        isPublic,
        suggests,
        currentSuggest,
        suggestsLoading,
        numberOfPosts: initialValues?.numberOfPosts,
        canShowInFeed,
    };

    const onChooseSuggest = useCallback(
        (suggest: string) => {
            setCurrentSuggest(suggest);
            trackDisplayNameEdit(suggest);
        },
        [trackDisplayNameEdit],
    );

    const loadSuggests = useCallback(async () => {
        if (!getSuggestMethod) {
            return;
        }
        let suggestNames;

        try {
            setSuggestsLoading(true);

            suggestNames = await getSuggestMethod();

            if (!suggestNames?.length) {
                throw new Error('Нет саджестов');
            }
        } catch (error) {
            showInFeed && trackShowInFeedEdit(true, false);
            setShowInFeed(false);
            dispatch(showNotificationAsync('Не удалось сгенерировать названия. Попробуйте позже'));
            setSuggestsLoading(false);

            return;
        }
        setSuggests(suggestNames);
        setCurrentSuggest(suggestNames[0]);
        trackDisplayNameEdit(suggestNames[0]);

        setSuggestsLoading(false);
    }, [dispatch, getSuggestMethod, showInFeed, trackDisplayNameEdit, trackShowInFeedEdit]);

    const onShowInFeedChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (suggestsLoading) {
                return;
            }

            const checked = e.currentTarget.checked;
            trackShowInFeedEdit(showInFeed, checked);
            setShowInFeed(checked);

            if (!checked) {
                return;
            }

            toggleIsPublic(true);

            loadSuggests();
        },
        [loadSuggests, showInFeed, suggestsLoading, toggleIsPublic, trackShowInFeedEdit],
    );

    const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCollectionName(e.currentTarget.value);
    }, []);

    const onIsPublicChange = useCallback(() => {
        trackIsPublicEdit(isPublic, !isPublic);

        if (isPublic) {
            showInFeed && trackShowInFeedEdit(true, false);
            setShowInFeed(false);
        }
        toggleIsPublic();
    }, [isPublic, showInFeed, toggleIsPublic, trackIsPublicEdit, trackShowInFeedEdit]);

    const handlers = {
        onIsPublicChange,
        onInputChange,
        onShowInFeedChange,
        onChooseSuggest,
    };

    useEffect(() => {
        if (needLoadSuggests && !currentSuggest && showInFeed && !suggestsLoading) {
            loadSuggests();
        }
    }, [currentSuggest, loadSuggests, needLoadSuggests, showInFeed, suggestsLoading]);

    return { values, handlers };
};
